import type { JournalEntryLine } from '@/services/accounting'

type TenderReconciliation = {
  tender: string | number
  txn_sum: string | number
  gap: string | number
}

export type CloverReconciliation = {
  gift_card_gap?: string | number | null
  gift_card_used_derived?: boolean | null
  card_batch_gap?: string | number
  batch_rounding?: string | number
  tax_variance?: string | number
  check?: TenderReconciliation
  cash?: TenderReconciliation
}

function toNumber(value: string | number | null | undefined): number {
  if (value == null || value === '') return 0
  const num = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(num) ? num : 0
}

function formatMoney(value: string | number | null | undefined): string {
  const num = toNumber(value)
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function isMeaningfulGap(value: string | number | null | undefined): boolean {
  return Math.abs(toNumber(value)) >= 0.005
}

function computeJeImbalance(lines: JournalEntryLine[]): {
  balanced: boolean
  label: string
} {
  const debits = lines
    .filter((line) => line.posting_type === 'Debit')
    .reduce((sum, line) => sum + toNumber(line.amount), 0)
  const credits = lines
    .filter((line) => line.posting_type === 'Credit')
    .reduce((sum, line) => sum + toNumber(line.amount), 0)
  const diff = Math.round((debits - credits) * 100) / 100
  if (Math.abs(diff) < 0.005) {
    return { balanced: true, label: 'Balanced' }
  }
  if (diff > 0) {
    return { balanced: false, label: `Credits short ${formatMoney(Math.abs(diff))}` }
  }
  return { balanced: false, label: `Debits short ${formatMoney(Math.abs(diff))}` }
}

function ReconRow({
  label,
  value,
  emphasize = false,
}: {
  label: string
  value: string
  emphasize?: boolean
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-x-4 text-sm">
      <span className="text-gray-500">{label}</span>
      <span
        className={`text-right tabular-nums ${
          emphasize ? 'font-medium text-amber-900' : 'text-gray-900'
        }`}
      >
        {value}
      </span>
    </div>
  )
}

type Props = {
  reconciliation?: CloverReconciliation | null
  lines: JournalEntryLine[]
}

export default function CloverReconciliationStrip({ reconciliation, lines }: Props) {
  const jeImbalance = computeJeImbalance(lines)
  const rows: Array<{ key: string; label: string; value: string; emphasize?: boolean }> = []

  rows.push({
    key: 'je',
    label: 'Draft JE balance',
    value: jeImbalance.label,
    emphasize: !jeImbalance.balanced,
  })

  if (reconciliation) {
    if (
      reconciliation.gift_card_gap != null &&
      (isMeaningfulGap(reconciliation.gift_card_gap) || reconciliation.gift_card_used_derived)
    ) {
      const derivedNote = reconciliation.gift_card_used_derived ? ' (derived)' : ''
      rows.push({
        key: 'gift-card',
        label: `Gift card gap${derivedNote}`,
        value: formatMoney(reconciliation.gift_card_gap),
        emphasize: isMeaningfulGap(reconciliation.gift_card_gap),
      })
    }

    if (reconciliation.card_batch_gap != null && isMeaningfulGap(reconciliation.card_batch_gap)) {
      rows.push({
        key: 'card-batch',
        label: 'Card batch gap',
        value: formatMoney(reconciliation.card_batch_gap),
        emphasize: true,
      })
    }

    if (
      reconciliation.batch_rounding != null &&
      isMeaningfulGap(reconciliation.batch_rounding)
    ) {
      rows.push({
        key: 'batch-rounding',
        label: 'Batch rounding booked',
        value: formatMoney(reconciliation.batch_rounding),
      })
    }

    if (reconciliation.tax_variance != null && isMeaningfulGap(reconciliation.tax_variance)) {
      rows.push({
        key: 'tax',
        label: 'Tax variance (collected − expected)',
        value: formatMoney(reconciliation.tax_variance),
        emphasize: true,
      })
    }

    for (const tenderKey of ['check', 'cash'] as const) {
      const tenderRecon = reconciliation[tenderKey]
      if (!tenderRecon) continue
      const label = tenderKey === 'check' ? 'Check' : 'Cash'
      if (isMeaningfulGap(tenderRecon.gap)) {
        rows.push({
          key: `${tenderKey}-gap`,
          label: `${label} tender vs txn sum`,
          value: `${formatMoney(tenderRecon.tender)} / ${formatMoney(tenderRecon.txn_sum)} (${formatMoney(tenderRecon.gap)})`,
          emphasize: true,
        })
      }
    }
  }

  if (jeImbalance.balanced && rows.length === 1) {
    return null
  }

  return (
    <div className="rounded-lg border border-amber-100 bg-amber-50/60 px-4 py-3">
      <h3 className="container-title">Reconciliation</h3>
      <p className="mt-1 text-xs text-gray-500">
        Diagnostic gaps to help locate penny drift between Clover reports and the draft JE.
      </p>
      <div className="mt-3 space-y-2">
        {rows.map((row) => (
          <ReconRow key={row.key} label={row.label} value={row.value} emphasize={row.emphasize} />
        ))}
      </div>
    </div>
  )
}
